import type { GeographicOption, GeographicTargetingState } from '@openx/types/targeting/geographic';
import { noop } from '@openx/utils/noop';
import omitBy from 'lodash/omitBy';
import { useMemo } from 'react';
import type { TargetingDropdownSectionProps } from '../shared/ReadOnlyTargetingDropdown/TargetingDropdownSection';
import type { TargetingItemsProps } from '../types';
import { GeographicItemsDefault } from './FormPreviewItems/GeographicItemsDefault';
import { GeographicItemsWithLocationSource } from './FormPreviewItems/GeographicItemsWithLocationSource';
import { useGeographicOptionsComponents } from './FormPreviewItems/hooks/useGeographicOptionsComponents';
import { useGeographicItemsData } from './hooks/useGeographicItemsData';
import { useGeographicOptions } from './hooks/useGeographicOptions';

export const GeographicFormPreviewItems = (
  props: TargetingItemsProps<GeographicTargetingState> &
    TargetingDropdownSectionProps & {
      onRemoveParameter?: (opt: GeographicOption) => void;
    }
) => {
  const {
    targetingParams,
    onOpenDrawerClick,
    onRemove,
    isDisabled,
    hideButtons,
    onRemoveParameter,
    optionsCount,
    disabledReasonMessage,
  } = props;
  const { isLocationSourceSelected } = useGeographicItemsData(targetingParams);

  const geographicOptionsComponents = useGeographicOptionsComponents({
    targetingParams,
    props,
  });

  const selectedInventories = Object.keys(omitBy(geographicOptionsComponents, item => item === null));

  const { optionsTypes, optionsNames } = useGeographicOptions({
    targetingParams,
    optionDropdown: null,
    onSelect: noop,
    onBulkApplyPostalCode: noop,
    onBulkApplyCoordinates: noop,
  });

  const geographicOptions = useMemo(() => {
    return Object.entries(geographicOptionsComponents).filter(element =>
      Object.keys(optionsTypes).includes(element[0])
    );
  }, [geographicOptionsComponents, optionsTypes]);

  if (isLocationSourceSelected) {
    return (
      <GeographicItemsWithLocationSource
        geographicOptions={geographicOptions}
        optionsNames={optionsNames}
        targetingParams={targetingParams}
        selectedInventories={selectedInventories}
        optionsCount={optionsCount}
        hideButtons={hideButtons}
        isDisabled={isDisabled}
        onOpenDrawerClick={onOpenDrawerClick}
        onRemove={onRemove}
        onRemoveParameter={onRemoveParameter}
        disabledReasonMessage={disabledReasonMessage}
      />
    );
  }

  return (
    <GeographicItemsDefault
      geographicOptions={geographicOptions}
      optionsNames={optionsNames}
      optionsCount={optionsCount}
      hideButtons={hideButtons}
      isDisabled={isDisabled}
      onOpenDrawerClick={onOpenDrawerClick}
      onRemove={onRemove}
      onRemoveParameter={onRemoveParameter}
      disabledReasonMessage={disabledReasonMessage}
    />
  );
};
